import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PollTextarea } from "@/components/ui/poll-textarea";
import { PollSelect } from "@/components/ui/poll-select";
import { PollRadio } from "@/components/ui/poll-radio";
import { PollCheckbox } from "@/components/ui/poll-checkbox";
import { PollStar } from "@/components/ui/poll-star";
import { PollNumbers } from "@/components/ui/poll-numbers";
import type { PollQuestionData } from "../PollQuestionsStep";
import MatrixQuestionTable from "../MatrixQuestionTable";
import styles from "./PollPreviewStep.module.scss";

interface BasicInfoFormData {
  pollName: string;
  pollDescription: string;
  introText?: string;
  closingText?: string;
  isAnonymous?: boolean;
}

interface PollPreviewStepProps {
  formData: BasicInfoFormData;
  questions: PollQuestionData[];
  className?: string;
}

// Store for preview answers (not persisted, just for UI interaction)
interface PreviewAnswers {
  [questionId: string]: string | string[] | number;
}

const PollPreviewStep: React.FC<PollPreviewStepProps> = ({
  formData,
  questions,
  className = "",
}) => {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<PreviewAnswers>({});

  const handleTextChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSelectChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleRadioChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleCheckboxChange = (
    questionId: string,
    optionValue: string,
    checked: boolean,
  ) => {
    setAnswers((prev) => {
      const currentValues = (prev[questionId] as string[]) || [];
      if (checked) {
        return { ...prev, [questionId]: [...currentValues, optionValue] };
      }
      return {
        ...prev,
        [questionId]: currentValues.filter((v) => v !== optionValue),
      };
    });
  };

  const handleStarChange = (questionId: string, value: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleNumberChange = (questionId: string, value: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  // Tablo sorusu her satırı ayrı soru sayılır; numaralar kursiyer ekranıyla aynı olsun.
  const startNumbers: number[] = [];
  let nextNumber = 1;
  for (const q of questions) {
    startNumbers.push(nextNumber);
    nextNumber +=
      q.type === "matrix"
        ? (q.rows ?? []).filter((r) => r.value.trim()).length
        : 1;
  }

  const renderQuestion = (question: PollQuestionData, index: number) => {
    const questionNumber = startNumbers[index];
    const questionText =
      question.questionText ||
      t("createPoll.preview.untitledQuestion");

    switch (question.type) {
      case "text":
        return (
          <div key={question.id} className={styles.questionItem}>
            <p className={styles.questionText}>
              {questionNumber}. {questionText}
            </p>
            <PollTextarea
              placeholder={t("createPoll.preview.textPlaceholder")}
              value={(answers[question.id] as string) || ""}
              onChange={(e) => handleTextChange(question.id, e.target.value)}
            />
          </div>
        );

      case "dropdown":
        return (
          <div key={question.id} className={styles.questionItem}>
            <p className={styles.questionText}>
              {questionNumber}. {questionText}
            </p>
            <PollSelect
              options={question.options
                .filter((opt) => opt.value.trim() !== "")
                .map((opt) => ({
                  label: opt.value,
                  value: opt.id,
                }))}
              value={(answers[question.id] as string) || null}
              onChange={(value) => handleSelectChange(question.id, value)}
              placeholder={t("createPoll.preview.selectPlaceholder")}
            />
          </div>
        );

      case "single_choice":
        return (
          <div key={question.id} className={styles.questionItem}>
            <p className={styles.questionText}>
              {questionNumber}. {questionText}
            </p>
            <div className={styles.optionsList}>
              {question.options
                .filter((opt) => opt.value.trim() !== "")
                .map((opt) => (
                  <PollRadio
                    key={opt.id}
                    name={`question-${question.id}`}
                    label={opt.value}
                    value={opt.id}
                    checked={(answers[question.id] as string) === opt.id}
                    onChange={(value) => handleRadioChange(question.id, value)}
                  />
                ))}
            </div>
          </div>
        );

      case "multiple_choice":
        return (
          <div key={question.id} className={styles.questionItem}>
            <p className={styles.questionText}>
              {questionNumber}. {questionText}
              <span className={styles.questionHint}>
                {" "}
                (
                {t("createPoll.preview.multipleChoiceHint")}
                )
              </span>
            </p>
            <div className={styles.optionsList}>
              {question.options
                .filter((opt) => opt.value.trim() !== "")
                .map((opt) => (
                  <PollCheckbox
                    key={opt.id}
                    label={opt.value}
                    checked={(
                      (answers[question.id] as string[]) || []
                    ).includes(opt.id)}
                    onChange={(checked) =>
                      handleCheckboxChange(question.id, opt.id, checked)
                    }
                  />
                ))}
            </div>
          </div>
        );

      case "star_rating":
        return (
          <div key={question.id} className={styles.questionItem}>
            <p className={styles.questionText}>
              {questionNumber}. {questionText}
            </p>
            <PollStar
              value={(answers[question.id] as number) || 0}
              onChange={(value) => handleStarChange(question.id, value)}
              maxStars={5}
              size={32}
            />
          </div>
        );

      case "scale":
        return (
          <div key={question.id} className={styles.questionItem}>
            <p className={styles.questionText}>
              {questionNumber}. {questionText}
            </p>
            <PollNumbers
              value={(answers[question.id] as number) || 0}
              onChange={(value) => handleNumberChange(question.id, value)}
              min={1}
              max={5}
              labelMin={t("createPoll.preview.scaleMin")}
              labelMax={t("createPoll.preview.scaleMax")}
            />
          </div>
        );

      case "matrix": {
        const rows = (question.rows ?? []).filter((r) => r.value.trim());
        const columns = question.options
          .filter((opt) => opt.value.trim() !== "")
          .map((opt) => opt.value);
        return (
          <div key={question.id} className={styles.questionItem}>
            <MatrixQuestionTable
              title={question.questionText}
              columns={columns}
              rows={rows.map((row, i) => {
                const answer = answers[row.id];
                return {
                  key: row.id,
                  number: questionNumber + i,
                  label: row.value,
                  required: question.required,
                  selectedIndex: typeof answer === "number" ? answer : null,
                };
              })}
              onSelect={(rowKey, columnIndex) =>
                setAnswers((prev) => ({ ...prev, [rowKey]: columnIndex }))
              }
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.leftColumn}>
        <div className={styles.pollInfoCard}>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>
              {t("createPoll.basicInfo.pollName")}
            </span>
            <span className={styles.infoValue}>
              {formData.pollName || t("createPoll.preview.noPollName")}
            </span>
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>
              {t("createPoll.basicInfo.pollDescription")}
            </span>
            <span className={styles.infoValue}>
              {formData.pollDescription ||
                t("createPoll.preview.noDescription")}
            </span>
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>
              {t("createPoll.basicInfo.isAnonymous")}
            </span>
            <span className={styles.infoValue}>
              {formData.isAnonymous
                ? t("polls.anonymous.yes")
                : t("polls.anonymous.no")}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.rightColumn}>
        {questions.length === 0 ? (
          <div className={styles.emptyState}>
            {t("createPoll.preview.noQuestions")}
          </div>
        ) : (
          <div className={styles.questionsList}>
            {formData.introText?.trim() && (
              <p className={styles.surveyText}>{formData.introText}</p>
            )}
            {questions.map((question, index) =>
              renderQuestion(question, index),
            )}
            {formData.closingText?.trim() && (
              <p className={`${styles.surveyText} ${styles.surveyTextClosing}`}>
                {formData.closingText}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PollPreviewStep;
